/**
 * Instance-level KPI scoring and weighted category rollup.
 *
 * PURE MODULE — no localStorage or React dependencies.
 * All data is received as parameters; storage access lives in
 * scheduleDataAdapter.ts (the adapter boundary).
 *
 * Flow:
 *   1. Caller provides SubKpiInstance[] config and per-instance data series
 *   2. For each instance: score it → { score_0_100, trend }
 *   3. Weighted-average all instance scores → single category KpiSignal
 *
 * Currently implemented for Schedule category. Other categories (Mission, Finance,
 * Data Reliability) can follow the same pattern.
 */

import type { KpiSignal } from "./types";
import { computeScheduleKpiSignal, type ProgressPoint, type ScheduleToleranceConfig } from "./schedule";
import { computeSpiHistorySeries, getCurrentSpiAsOf, getSpiColorBand } from "./schedule";
import { KPI_DEFAULTS } from "./defaults";
import {
  computeCostKpiSignal,
  computeCpiHistorySeries,
  getCurrentCpiAsOf,
  getCpiColorBand,
  type CostPoint,
  type CostToleranceConfig,
} from "./budget";
import type { ScheduleProgressPoint } from "../Data/ScheduleProgress/scheduleProgressOverrides";
import type { FinanceCostPoint } from "../Data/Budget/budgetOverrides";
import type { SubKpiInstance } from "../Data/ProjectConfig/projectKpiConfigOverrides";

// Re-export types needed by adapter and callers.
export type { SubKpiInstance } from "../Data/ProjectConfig/projectKpiConfigOverrides";
export type { ScheduleProgressPoint } from "../Data/ScheduleProgress/scheduleProgressOverrides";
export type { FinanceCostPoint } from "../Data/Budget/budgetOverrides";

// ---------------------------------------------------------------------------
// Per-instance scoring
// ---------------------------------------------------------------------------

export type InstanceScore = {
  instance_guid: string;
  display_name: string;
  catalog_kpi_guid: string;
  weight: number;
  score_0_100: number | null;
  color_code: number; // 0=no-data, 1=red, 2=yellow, 3=green
  trend: -1 | 0 | 1;
};

/**
 * Convert raw ScheduleProgressPoint[] to the ProgressPoint[] format
 * consumed by the schedule scoring engine.
 *
 * IMPORTANT: Rows where actual_complete is null are EXCLUDED because null
 * means "no actual progress entered yet", not "0% complete". Including
 * them would falsely report behind-schedule variance against the baseline.
 * The chart treats these the same way — no variance is shown until the
 * user enters actual values.
 */
function toProgressPoints(
  projectGuid: string,
  rawSeries: ScheduleProgressPoint[],
): ProgressPoint[] {
  return rawSeries
    .filter((r) => r.reporting_date && r.baseline_complete !== null && r.actual_complete !== null)
    .map((r) => ({
      project_GUID: projectGuid,
      reporting_date: r.reporting_date,
      baseline_complete: r.baseline_complete ?? 0,
      actual_complete: r.actual_complete ?? 0,
    }));
}

/**
 * Score a single Schedule Progress instance.
 *
 * Pure function — caller provides the data series.
 */
function scoreScheduleProgressInstance(
  projectGuid: string,
  inst: SubKpiInstance,
  asOfDate: string,
  globalScheduleConfig: ScheduleToleranceConfig,
  rawSeries: ScheduleProgressPoint[],
): InstanceScore {
  const instanceGuid = String(inst.instance_guid || "").trim();

  const points = toProgressPoints(projectGuid, rawSeries);

  // Build per-instance tolerance config (fall back to global defaults).
  const instanceConfig: ScheduleToleranceConfig = {
    yellow_threshold_pp: inst.variance_yellow_pp ?? globalScheduleConfig.yellow_threshold_pp,
    red_threshold_pp: inst.variance_red_pp ?? globalScheduleConfig.red_threshold_pp,
    score_zero_at_behind_pp: globalScheduleConfig.score_zero_at_behind_pp,
    trend_epsilon_pp: globalScheduleConfig.trend_epsilon_pp,
  };

  const signal = computeScheduleKpiSignal(points, asOfDate, instanceConfig);

  return {
    instance_guid: instanceGuid,
    display_name: String(inst.display_name || ""),
    catalog_kpi_guid: String(inst.catalog_kpi_guid || ""),
    weight: typeof inst.weight === "number" && Number.isFinite(inst.weight) ? inst.weight : 0,
    score_0_100: signal.score_0_100,
    color_code: signal.indicator_color_value,
    trend: signal.indicator_trend_value,
  };
}

/**
 * Score a single Schedule SPI (variance) instance.
 *
 * Pure function — caller provides the data series.
 */
function scoreScheduleSpiInstance(
  projectGuid: string,
  inst: SubKpiInstance,
  asOfDate: string,
  _globalScheduleConfig: ScheduleToleranceConfig,
  rawSeries: ScheduleProgressPoint[],
): InstanceScore {
  const instanceGuid = String(inst.instance_guid || "").trim();

  const points = toProgressPoints(projectGuid, rawSeries);

  // Compute SPI history.
  // Pass the per-instance early maturity guard so that the scoring pipeline
  // suppresses SPI at the same threshold the UI graph uses.
  const spiHistory = computeSpiHistorySeries(points, {
    warning_threshold: inst.spi_warning_threshold ?? KPI_DEFAULTS.spi.warning_threshold,
    critical_threshold: inst.spi_critical_threshold ?? KPI_DEFAULTS.spi.critical_threshold,
    early_maturity_guard_pct: typeof (inst as any).spi_min_baseline_pct === "number"
      ? (inst as any).spi_min_baseline_pct
      : KPI_DEFAULTS.spi.early_maturity_guard_pct,   // match UI default — do NOT pass undefined (it would override SPI_TOLERANCE_DEFAULTS via spread)
  });

  const current = getCurrentSpiAsOf(spiHistory, asOfDate);

  if (!current) {
    return {
      instance_guid: instanceGuid,
      display_name: String(inst.display_name || ""),
      catalog_kpi_guid: String(inst.catalog_kpi_guid || ""),
      weight: typeof inst.weight === "number" && Number.isFinite(inst.weight) ? inst.weight : 0,
      score_0_100: null,
      color_code: 0,
      trend: 0,
    };
  }

  const colorBand = getSpiColorBand(current.spi, {
    warning_threshold: inst.spi_warning_threshold ?? 0.95,
    critical_threshold: inst.spi_critical_threshold ?? 0.90,
  });

  // Convert SPI (ratio) to a 0-100 score.
  // SPI=1.0 is perfect (score 100). SPI=0.90 is critical threshold.
  // Clamp to 0-100.
  const spiScore = Math.round(Math.max(0, Math.min(100, current.spi * 100)));

  // Trend: compare current SPI to the previous point.
  let trend: -1 | 0 | 1 = 0;
  if (spiHistory.length >= 2) {
    const prevIdx = spiHistory.findIndex((p) => p.date === current.date) - 1;
    if (prevIdx >= 0) {
      const prevSpi = spiHistory[prevIdx].spi;
      const delta = current.spi - prevSpi;
      if (delta > 0.01) trend = 1;
      else if (delta < -0.01) trend = -1;
    }
  }

  return {
    instance_guid: instanceGuid,
    display_name: String(inst.display_name || ""),
    catalog_kpi_guid: String(inst.catalog_kpi_guid || ""),
    weight: typeof inst.weight === "number" && Number.isFinite(inst.weight) ? inst.weight : 0,
    score_0_100: spiScore,
    color_code: colorBand,
    trend,
  };
}

/**
 * Score a single Schedule instance (dispatches to the correct scorer
 * based on catalog_kpi_guid).
 *
 * Pure function — caller provides the data series for this instance.
 */
export function scoreScheduleInstance(
  projectGuid: string,
  inst: SubKpiInstance,
  asOfDate: string,
  globalScheduleConfig: ScheduleToleranceConfig,
  rawSeries: ScheduleProgressPoint[],
): InstanceScore {
  const catalog = String(inst.catalog_kpi_guid || "").trim();

  if (catalog === "kpi_sched_variance_v1") {
    return scoreScheduleSpiInstance(projectGuid, inst, asOfDate, globalScheduleConfig, rawSeries);
  }

  // Default: Progress-style scoring (covers kpi_sched_progress_v1 and any others).
  return scoreScheduleProgressInstance(projectGuid, inst, asOfDate, globalScheduleConfig, rawSeries);
}

// ---------------------------------------------------------------------------
// Category-level weighted rollup
// ---------------------------------------------------------------------------

/**
 * Derive category color from the per-instance PP-based color_code values
 * using a weighted average (same weighting as the score rollup).
 *
 * This keeps the category color consistent with the per-instance colors,
 * which are derived from the actual behind-pp vs. tolerance thresholds
 * in the scoring engine. A previous approach re-derived color from the
 * weighted-average *score*, which used fixed 80/60 thresholds that could
 * disagree with the PP-based mapping (e.g. 16–20pp behind mapped to
 * score <60 = red, but PP thresholds said yellow).
 */
function weightedColorFromInstances(
  scorable: InstanceScore[],
  totalWeight: number,
): number {
  if (scorable.length === 0 || totalWeight <= 0) return 0;

  // Single-instance fast path: just use its PP-based color directly.
  if (scorable.length === 1) return scorable[0].color_code;

  // Weighted average of color codes; round to nearest discrete code.
  let wc = 0;
  for (const is of scorable) {
    wc += is.color_code * (is.weight / totalWeight);
  }
  // Snap to discrete: ≥2.5 → green(3), ≥1.5 → yellow(2), ≥0.5 → red(1), else 0.
  if (wc >= 2.5) return 3;
  if (wc >= 1.5) return 2;
  if (wc >= 0.5) return 1;
  return 0;
}

/**
 * Compute a category-level KpiSignal by scoring each instance and
 * performing a weighted rollup.
 *
 * Pure function — caller provides instances config and a Map of
 * per-instance data series (keyed by instance_guid, "" for default).
 *
 * If no instances are provided or all have zero weight, returns null
 * (caller should fall back to the existing single-score engine).
 */
export function computeScheduleCategoryFromInstances(
  projectGuid: string,
  asOfDate: string,
  globalScheduleConfig: ScheduleToleranceConfig,
  instances: SubKpiInstance[],
  seriesByInstance: Map<string, ScheduleProgressPoint[]>,
): { signal: KpiSignal; instanceScores: InstanceScore[] } | null {
  const pg = String(projectGuid || "").trim();
  if (!pg) return null;

  if (instances.length === 0) return null;

  // Score each instance using data from the provided map.
  // When schedule_template_type is "progress_spi", produce TWO scored items
  // from the same data: one for Progress (behind-pp) and one for SPI (ratio).
  // The instance weight is split between them per spi_weight_pct (default 50%).
  const instanceScores: InstanceScore[] = [];
  for (const inst of instances) {
    const key = String(inst.instance_guid || "").trim();
    const series = seriesByInstance.get(key) ?? [];
    const tmplType = String((inst as any).schedule_template_type || "").trim();

    if (tmplType === "progress_spi") {
      const totalWeight = typeof inst.weight === "number" && Number.isFinite(inst.weight) ? inst.weight : 0;
      const spiPct = typeof (inst as any).spi_weight_pct === "number"
        && Number.isFinite((inst as any).spi_weight_pct)
        ? (inst as any).spi_weight_pct
        : 50; // default 50/50 split

      // Attempt SPI score first to determine if it can contribute.
      const spiScore = scoreScheduleSpiInstance(pg, inst, asOfDate, globalScheduleConfig, series);
      const spiHasData = spiScore.score_0_100 !== null;

      // Progress score (behind-pp formula).
      // If SPI is suppressed (baseline below guard threshold → null score),
      // Progress absorbs the full instance weight.
      const progressScore = scoreScheduleProgressInstance(pg, inst, asOfDate, globalScheduleConfig, series);
      progressScore.weight = spiHasData
        ? totalWeight * (100 - spiPct) / 100
        : totalWeight;
      progressScore.display_name = `${inst.display_name || "Progress"} (Progress)`;
      instanceScores.push(progressScore);

      // SPI score (actual/baseline ratio).
      // Only contributes weight when baseline is above the guard threshold.
      spiScore.weight = spiHasData
        ? totalWeight * spiPct / 100
        : 0;
      spiScore.display_name = `${inst.display_name || "Progress"} (SPI)`;
      spiScore.catalog_kpi_guid = "kpi_sched_variance_v1";
      /* DEV-814 unification: in progress_spi template, both Progress and SPI
       * scores are derived from the same underlying user data (actual vs
       * baseline). Their trends should be consistent — when the user hasn't
       * entered new data, both must show no-change. The Progress trend uses
       * the corrected logic in schedule.ts (user-input override + behindPP
       * delta with 1.0pp epsilon). The SPI trend was using its own delta
       * comparison with epsilon=0.01 which could disagree with Progress.
       * Aligning here so the weighted average reflects one consistent signal. */
      spiScore.trend = progressScore.trend;
      instanceScores.push(spiScore);
    } else {
      instanceScores.push(scoreScheduleInstance(pg, inst, asOfDate, globalScheduleConfig, series));
    }
  }

  // Filter to instances with valid scores and non-zero weight.
  const scorable = instanceScores.filter(
    (is) => is.score_0_100 !== null && is.weight > 0,
  );

  if (scorable.length === 0) {
    // All instances are informational-only or have no data.
    // Check if any have data at all (even without weight).
    const anyWithData = instanceScores.filter((is) => is.score_0_100 !== null);
    if (anyWithData.length === 0) {
      return {
        signal: {
          score_0_100: null,
          indicator_color_value: 0,
          indicator_trend_value: 0,
          as_of_date: asOfDate,
          explain: { headline: "No schedule instance data available." },
        },
        instanceScores,
      };
    }

    // If there are instances with data but all have zero weight,
    // use an unweighted average as a reasonable fallback.
    const avgScore =
      anyWithData.reduce((sum, is) => sum + (is.score_0_100 ?? 0), 0) / anyWithData.length;
    const rounded = Math.round(avgScore);
    const trendSum = anyWithData.reduce((sum, is) => sum + is.trend, 0);
    const trendAvg = trendSum / anyWithData.length;
    const trend: -1 | 0 | 1 = trendAvg > 0.1 ? 1 : trendAvg < -0.1 ? -1 : 0;

    // Color: use worst (minimum) PP-based color from instances with data.
    const worstColor = anyWithData.reduce(
      (min, is) => (is.color_code > 0 && is.color_code < min ? is.color_code : min),
      3,
    );

    return {
      signal: {
        score_0_100: rounded,
        indicator_color_value: worstColor,
        indicator_trend_value: trend,
        as_of_date: asOfDate,
        explain: {
          headline: `Schedule score ${rounded}/100 (unweighted avg of ${anyWithData.length} instance(s) — weights not set).`,
          details: {
            instances: instanceScores.map((is) => ({
              name: is.display_name,
              score: is.score_0_100,
              weight: is.weight,
              color: is.color_code,
              trend: is.trend,
            })),
          },
        },
      },
      instanceScores,
    };
  }

  // Weighted average.
  const totalWeight = scorable.reduce((sum, is) => sum + is.weight, 0);
  let weightedScore = 0;
  let weightedTrend = 0;

  for (const is of scorable) {
    const w = is.weight / totalWeight;
    weightedScore += (is.score_0_100 ?? 0) * w;
    weightedTrend += is.trend * w;
  }

  const rounded = Math.round(weightedScore);
  const trend: -1 | 0 | 1 = weightedTrend > 0.1 ? 1 : weightedTrend < -0.1 ? -1 : 0;

  // Category color from weighted per-instance PP-based colors (not score thresholds).
  const categoryColor = weightedColorFromInstances(scorable, totalWeight);

  return {
    signal: {
      score_0_100: rounded,
      indicator_color_value: categoryColor,
      indicator_trend_value: trend,
      as_of_date: asOfDate,
      explain: {
        headline: `Schedule score ${rounded}/100 (weighted avg of ${scorable.length} instance(s)).`,
        details: {
          instances: instanceScores.map((is) => ({
            name: is.display_name,
            score: is.score_0_100,
            weight: is.weight,
            color: is.color_code,
            trend: is.trend,
          })),
          total_weight: totalWeight,
        },
      },
    },
    instanceScores,
  };
}

// ===========================================================================
// FINANCE / BUDGET — Per-instance scoring & category rollup
// ===========================================================================

/**
 * Convert raw FinanceCostPoint[] to the CostPoint[] format consumed by the
 * finance scoring engine.
 */
function toCostPoints(
  projectGuid: string,
  rawSeries: FinanceCostPoint[],
): CostPoint[] {
  return rawSeries
    .filter(
      (r) =>
        r.reporting_date &&
        r.baseline_budget !== null &&
        r.actual_spend !== null,
    )
    .map((r) => ({
      project_GUID: projectGuid,
      reporting_date: r.reporting_date,
      baseline_budget: r.baseline_budget ?? 0,
      actual_spend: r.actual_spend ?? 0,
    }));
}

/**
 * Score a single Finance/Budget Progress instance (cost variance).
 */
function scoreFinanceProgressInstance(
  projectGuid: string,
  inst: SubKpiInstance,
  asOfDate: string,
  globalCostConfig: CostToleranceConfig,
  rawSeries: FinanceCostPoint[],
): InstanceScore {
  const instanceGuid = String(inst.instance_guid || "").trim();
  const points = toCostPoints(projectGuid, rawSeries);

  const instanceConfig: CostToleranceConfig = {
    yellow_threshold_pct:
      (inst as any).variance_cost_yellow_pct ??
      globalCostConfig.yellow_threshold_pct,
    red_threshold_pct:
      (inst as any).variance_cost_red_pct ??
      globalCostConfig.red_threshold_pct,
    score_zero_at_over_pct: globalCostConfig.score_zero_at_over_pct,
    trend_epsilon_pct: globalCostConfig.trend_epsilon_pct,
  };

  const signal = computeCostKpiSignal(points, asOfDate, instanceConfig);

  return {
    instance_guid: instanceGuid,
    display_name: String(inst.display_name || ""),
    catalog_kpi_guid: String(inst.catalog_kpi_guid || ""),
    weight:
      typeof inst.weight === "number" && Number.isFinite(inst.weight)
        ? inst.weight
        : 0,
    score_0_100: signal.score_0_100,
    color_code: signal.indicator_color_value,
    trend: signal.indicator_trend_value,
  };
}

/**
 * Score a single Finance CPI (cost performance index) instance.
 */
function scoreFinanceCpiInstance(
  projectGuid: string,
  inst: SubKpiInstance,
  asOfDate: string,
  _globalCostConfig: CostToleranceConfig,
  rawSeries: FinanceCostPoint[],
): InstanceScore {
  const instanceGuid = String(inst.instance_guid || "").trim();
  const points = toCostPoints(projectGuid, rawSeries);

  // Pass the per-instance early maturity guard so that the scoring pipeline
  // suppresses CPI at the same threshold the UI graph uses.
  const cpiHistory = computeCpiHistorySeries(points, {
    warning_threshold: (inst as any).cpi_warning_threshold ?? KPI_DEFAULTS.cpi.warning_threshold,
    critical_threshold: (inst as any).cpi_critical_threshold ?? KPI_DEFAULTS.cpi.critical_threshold,
    early_maturity_guard_pct: typeof (inst as any).cpi_min_baseline_pct === "number"
      ? (inst as any).cpi_min_baseline_pct
      : KPI_DEFAULTS.cpi.early_maturity_guard_pct,   // match UI default — do NOT pass undefined (it would override CPI_TOLERANCE_DEFAULTS via spread)
  });

  const current = getCurrentCpiAsOf(cpiHistory, asOfDate);

  if (!current) {
    return {
      instance_guid: instanceGuid,
      display_name: String(inst.display_name || ""),
      catalog_kpi_guid: String(inst.catalog_kpi_guid || ""),
      weight:
        typeof inst.weight === "number" && Number.isFinite(inst.weight)
          ? inst.weight
          : 0,
      score_0_100: null,
      color_code: 0,
      trend: 0,
    };
  }

  const colorBand = getCpiColorBand(current.cpi, {
    warning_threshold: (inst as any).cpi_warning_threshold ?? 0.95,
    critical_threshold: (inst as any).cpi_critical_threshold ?? 0.90,
  });

  // CPI=1.0 is perfect (score 100). Clamp to 0–100.
  const cpiScore = Math.round(
    Math.max(0, Math.min(100, current.cpi * 100)),
  );

  let trend: -1 | 0 | 1 = 0;
  if (cpiHistory.length >= 2) {
    const prevIdx =
      cpiHistory.findIndex((p) => p.date === current.date) - 1;
    if (prevIdx >= 0) {
      const prevCpi = cpiHistory[prevIdx].cpi;
      const delta = current.cpi - prevCpi;
      if (delta > 0.01) trend = 1;
      else if (delta < -0.01) trend = -1;
    }
  }

  return {
    instance_guid: instanceGuid,
    display_name: String(inst.display_name || ""),
    catalog_kpi_guid: String(inst.catalog_kpi_guid || ""),
    weight:
      typeof inst.weight === "number" && Number.isFinite(inst.weight)
        ? inst.weight
        : 0,
    score_0_100: cpiScore,
    color_code: colorBand,
    trend,
  };
}

/**
 * Score a single Finance instance (dispatches based on catalog_kpi_guid).
 */
export function scoreFinanceInstance(
  projectGuid: string,
  inst: SubKpiInstance,
  asOfDate: string,
  globalCostConfig: CostToleranceConfig,
  rawSeries: FinanceCostPoint[],
): InstanceScore {
  const catalog = String(inst.catalog_kpi_guid || "").trim();

  if (catalog === "kpi_cost_variance_v1") {
    return scoreFinanceCpiInstance(
      projectGuid,
      inst,
      asOfDate,
      globalCostConfig,
      rawSeries,
    );
  }

  // Default: Progress-style scoring (covers kpi_cost_progress_v1 and kpi_sched_progress_v1 reused for finance).
  return scoreFinanceProgressInstance(
    projectGuid,
    inst,
    asOfDate,
    globalCostConfig,
    rawSeries,
  );
}

/**
 * Compute a category-level KpiSignal for Finance/Budget by scoring each
 * instance and performing a weighted rollup.
 *
 * Same rollup logic as computeScheduleCategoryFromInstances.
 */
export function computeFinanceCategoryFromInstances(
  projectGuid: string,
  asOfDate: string,
  globalCostConfig: CostToleranceConfig,
  instances: SubKpiInstance[],
  seriesByInstance: Map<string, FinanceCostPoint[]>,
): { signal: KpiSignal; instanceScores: InstanceScore[] } | null {
  const pg = String(projectGuid || "").trim();
  if (!pg) return null;
  if (instances.length === 0) return null;

  // Score each instance, supporting "progress_cpi" template split (like schedule's progress_spi).
  const instanceScores: InstanceScore[] = [];
  for (const inst of instances) {
    const key = String(inst.instance_guid || "").trim();
    const series = seriesByInstance.get(key) ?? [];
    const tmplType = String(
      (inst as any).budget_template_type || "",
    ).trim();

    if (tmplType === "progress_cpi") {
      const totalWeight =
        typeof inst.weight === "number" && Number.isFinite(inst.weight)
          ? inst.weight
          : 0;
      const cpiPct =
        typeof (inst as any).cpi_weight_pct === "number" &&
        Number.isFinite((inst as any).cpi_weight_pct)
          ? (inst as any).cpi_weight_pct
          : 50;

      const cpiScore = scoreFinanceCpiInstance(
        pg,
        inst,
        asOfDate,
        globalCostConfig,
        series,
      );
      const cpiHasData = cpiScore.score_0_100 !== null;

      const progressScore = scoreFinanceProgressInstance(
        pg,
        inst,
        asOfDate,
        globalCostConfig,
        series,
      );
      progressScore.weight = cpiHasData
        ? (totalWeight * (100 - cpiPct)) / 100
        : totalWeight;
      progressScore.display_name = `${inst.display_name || "Budget"} (Progress)`;
      instanceScores.push(progressScore);

      cpiScore.weight = cpiHasData ? (totalWeight * cpiPct) / 100 : 0;
      cpiScore.display_name = `${inst.display_name || "Budget"} (CPI)`;
      cpiScore.catalog_kpi_guid = "kpi_cost_variance_v1";
      instanceScores.push(cpiScore);
    } else {
      instanceScores.push(
        scoreFinanceInstance(pg, inst, asOfDate, globalCostConfig, series),
      );
    }
  }

  // Filter to instances with valid scores and non-zero weight.
  const scorable = instanceScores.filter(
    (is) => is.score_0_100 !== null && is.weight > 0,
  );

  if (scorable.length === 0) {
    const anyWithData = instanceScores.filter(
      (is) => is.score_0_100 !== null,
    );
    if (anyWithData.length === 0) {
      return {
        signal: {
          score_0_100: null,
          indicator_color_value: 0,
          indicator_trend_value: 0,
          as_of_date: asOfDate,
          explain: { headline: "No budget instance data available." },
        },
        instanceScores,
      };
    }

    const avgScore =
      anyWithData.reduce((sum, is) => sum + (is.score_0_100 ?? 0), 0) /
      anyWithData.length;
    const rounded = Math.round(avgScore);
    const trendSum = anyWithData.reduce((sum, is) => sum + is.trend, 0);
    const trendAvg = trendSum / anyWithData.length;
    const trend: -1 | 0 | 1 =
      trendAvg > 0.1 ? 1 : trendAvg < -0.1 ? -1 : 0;
    const worstColor = anyWithData.reduce(
      (min, is) =>
        is.color_code > 0 && is.color_code < min ? is.color_code : min,
      3,
    );

    return {
      signal: {
        score_0_100: rounded,
        indicator_color_value: worstColor,
        indicator_trend_value: trend,
        as_of_date: asOfDate,
        explain: {
          headline: `Budget score ${rounded}/100 (unweighted avg of ${anyWithData.length} instance(s) — weights not set).`,
          details: {
            instances: instanceScores.map((is) => ({
              name: is.display_name,
              score: is.score_0_100,
              weight: is.weight,
              color: is.color_code,
              trend: is.trend,
            })),
          },
        },
      },
      instanceScores,
    };
  }

  // Weighted average.
  const totalWeight = scorable.reduce((sum, is) => sum + is.weight, 0);
  let weightedScore = 0;
  let weightedTrend = 0;

  for (const is of scorable) {
    const w = is.weight / totalWeight;
    weightedScore += (is.score_0_100 ?? 0) * w;
    weightedTrend += is.trend * w;
  }

  const rounded = Math.round(weightedScore);
  const trend: -1 | 0 | 1 =
    weightedTrend > 0.1 ? 1 : weightedTrend < -0.1 ? -1 : 0;
  const categoryColor = weightedColorFromInstances(scorable, totalWeight);

  return {
    signal: {
      score_0_100: rounded,
      indicator_color_value: categoryColor,
      indicator_trend_value: trend,
      as_of_date: asOfDate,
      explain: {
        headline: `Budget score ${rounded}/100 (weighted avg of ${scorable.length} instance(s)).`,
        details: {
          instances: instanceScores.map((is) => ({
            name: is.display_name,
            score: is.score_0_100,
            weight: is.weight,
            color: is.color_code,
            trend: is.trend,
          })),
          total_weight: totalWeight,
        },
      },
    },
    instanceScores,
  };
}
